import React, { useState } from "react";
import { EditIcon } from "../../assets/icons/EditIcon";
import { DeleteIcon } from "../../assets/icons/DeleteIcon";
import { formatDateTimeAsStored } from "../../utils/utils";

const DealList = ({ dealData, handleDealSelection, onEditDeal, onDeleteDeal }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle column sorting
  const handleSort = (field) => {
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortDirection(newDirection);
  };

  // Get sorted data
  const getSortedData = (data) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle dates
      if (
        sortField === "createdAt" ||
        sortField === "documentDate" ||
        sortField === "closingDate" ||
        sortField === "filingDate"
      ) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numbers
      if (sortField === "id") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Handle strings
      if (typeof aVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal ? bVal.toLowerCase() : "";
      }

      // Handle null/undefined values
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Calculate total pages
  const totalPages = Math.ceil(dealData.length / itemsPerPage);

  // Get sorted and paginated items
  const sortedData = getSortedData(dealData);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="note-container">
      <table className="note-table">
        <thead>
          <tr>
            <th
              onClick={() => handleSort("id")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Deal Id{" "}
              {sortField === "id" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("deal")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Deal Name{" "}
              {sortField === "deal" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("dealCIK")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Deal CIK{" "}
              {sortField === "dealCIK" && (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("depositorName")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Depositor Name{" "}
              {sortField === "depositorName" &&
                (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("depositorCIK")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Depositor CIK{" "}
              {sortField === "depositorCIK" &&
                (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("accessionNumber")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Accession Number{" "}
              {sortField === "accessionNumber" &&
                (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("primaryDocument")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Primary Document{" "}
              {sortField === "primaryDocument" &&
                (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("documentDate")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Document Date{" "}
              {sortField === "documentDate" &&
                (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("closingDate")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Closing Date{" "}
              {sortField === "closingDate" &&
                (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th
              onClick={() => handleSort("createdAt")}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              Created{" "}
              {sortField === "createdAt" &&
                (sortDirection === "asc" ? "▲" : "▼")}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length > 0 ? (
            currentItems.map((deal, index) => {
              const handleDelete = (e) => {
                e.stopPropagation();
                onDeleteDeal?.(deal);
              };
              // Generate SEC filing link
              const formattedAccessionNo =
                deal.accessionNumber?.replace(/-/g, "") || "";
              const depositorCIK = deal.depositorCIK;
              const primaryDocument = deal.primaryDocument;

              const secLink =
                depositorCIK && formattedAccessionNo && primaryDocument
                  ? `https://www.sec.gov/Archives/edgar/data/${depositorCIK}/${formattedAccessionNo}/${primaryDocument}`
                  : null;

              return (
                <tr key={index} onClick={() => handleDealSelection(deal.id)}>
                  <td>{deal.id}</td>
                  <td>{deal.deal}</td>
                  <td>{deal.dealCIK}</td>
                  <td>{deal.depositorName}</td>
                  <td>{deal.depositorCIK}</td>
                  <td>{deal.accessionNumber}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {secLink ? (
                      <a className="tx-hash" href={secLink} target="_blank" rel="noopener noreferrer" title={primaryDocument}>
                        {primaryDocument}
                      </a>
                    ) : (
                      <span className="muted">{primaryDocument || "—"}</span>
                    )}
                  </td>
                  <td>
                    {formatDateTimeAsStored(deal.documentDate)}
                  </td>
                  <td>
                    {formatDateTimeAsStored(deal.closingDate)}
                  </td>
                  <td>
                    {formatDateTimeAsStored(deal.createdAt)}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: "inline-flex", gap: "8px", alignItems: "center" }}>
                      <div
                        onClick={() => onEditDeal?.(deal)}
                        style={{ cursor: "pointer" }}
                        aria-label="Edit deal"
                      >
                        <EditIcon />
                      </div>
                      <div
                        onClick={handleDelete}
                        style={{ cursor: "pointer" }}
                        aria-label="Delete deal"
                      >
                        <DeleteIcon />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="11">No deals available</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="pagination">
        <button
          className="arrow-button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &larr;
        </button>

        {(() => {
          const pages = [];
          let startPage = Math.max(1, currentPage - 2);
          let endPage = Math.min(totalPages, startPage + 4);

          // Adjust start page if we're near the end
          if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
          }

          for (let i = startPage; i <= endPage; i++) {
            pages.push(
              <button
                key={i}
                onClick={() => handlePageChange(i)}
                className={`page-button ${currentPage === i ? "active" : ""}`}
              >
                {i}
              </button>
            );
          }

          return pages;
        })()}

        <button
          className="arrow-button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &rarr;
        </button>
      </div>
    </div>
  );
};

export default DealList;
